import React, { useEffect, useCallback } from 'react'
import { Box, Grid, Divider } from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { store } from 'reduxStore'
import api from 'api'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import Button from 'components/Common/Button'
import { SECONDARY_BUTTON } from 'themes/theme'
import ThreeDotsLoadingSpinner from 'components/Common/LoadingSpinner/threeDots'

import Dialog from '@mui/material/Dialog'
import ZendeskLink from 'components/Common/ZendeskLink'
import moment from 'moment'

const sx = {
  root: {
    color: 'var(--primary)',
    fontFamily: 'Poppins',
  },
  label: {
    fontSize: 14,
    fontWeight: 'bold',
    letterSpacing: -0.37,
    lineHeight: '19px',
    marginBottom: theme => theme.spacing(2),
  },
  text: {
    fontSize: '14px',
  },
  bold: {
    fontWeight: 'bold',
    fontSize: '14px',
  },
  yourPlanContainer: {
    marginBottom: theme => theme.spacing(7),
  },
  button: {
    color: 'var(--blue)',
    border: '#fff',
  },
  close: {
    cursor: 'pointer',
    fontSize: '',
  },
}

const UnCancelSubscriptionContent = ({ onClose, onCanceled }) => {
  return (
    <Box sx={sx.root}>
      <Box sx={sx.label} fontSize="20px" textAlign="center" marginBottom="5%">
        Subscription cancellation
      </Box>
      <Divider xs={{ marginY: 5 }} />
      <p className="text-center mt-5 font font14px">
        When you cancel, you'll lose access to the Markaaz platform at the end of your billing cycle.
      </p>
      <div className="d-flex flex-column flex-md-row gap-3 justify-content-center mt-5 pt-5">
        <Button
          sx={{
            whiteSpace: 'nowrap',
          }}
          onClick={onCanceled}
          negative
        >
          Cancel Subscription
        </Button>
        <Button onClick={onClose}>Keep Subscription</Button>
      </div>
    </Box>
  )
}

const CancelSubscriptionSuccessContent = ({ onClose }) => {
  return (
    <Box sx={sx.root}>
      <Box sx={sx.label} fontSize="20px" textAlign="center" marginBottom="5%">
        Your cancellation has been successful.
      </Box>
      <Divider xs={{ marginY: 5 }} />
      <p className="text-center mt-5 font14px">
        You will have full access to your subscription until the end of your billing cycle, at which point your card
        will no longer be charged.
      </p>
      <div className="d-flex flex-column align-items-center gap-3 justify-content-center mt-5 pt-5">
        <CheckCircleIcon
          sx={{
            fontSize: '15rem',
            color: 'rgb(93,217,193)',
          }}
        />
        <Button onClick={onClose}>Ok</Button>
      </div>
    </Box>
  )
}

const Subscription = props => {
  const user = store.getState().userReducer.user
  const [isOpen, setIsOpen] = React.useState(false)
  const [cancelSubscription, setCancelSubscription] = React.useState(false)
  const [activeSubscription, setActiveSubscription] = React.useState({})
  const [billingHistory, setBillingHistory] = React.useState([])
  const [loading, setLoading] = React.useState(true)
  const { accountType } = user

  const dashboardInfo = useSelector(state => state.apiResponseReducers.dashboardInfo)
  const subscriptionType = dashboardInfo.user_info.subscriptionType
  const markaazType = subscriptionType === 'Markaaz' || subscriptionType === null || subscriptionType === ''
  const navigate = useNavigate()

  const handleCancel = useCallback(() => {
    api.cancelSubscription()
    setCancelSubscription(true)
  }, [])

  const onClickStartNow = e => {
    e && e.preventDefault()
    navigate('/welcome')
  }

  const onClickCancel = () => {
    setIsOpen(true)
  }

  const onClose = () => {
    setIsOpen(false)
    setLoading(true)
    fetchSubscriptions()
  }

  const fetchSubscriptions = useCallback(async () => {
    try {
      if (markaazType) {
        const { subscriptions, billingHistory } = await api.getSubscriptionState()
        setActiveSubscription(subscriptions?.length > 0 ? subscriptions[0] : {})
        setBillingHistory(billingHistory)
        setLoading(false)
      } else {
        setLoading(false)
      }
    } catch (err) {
      console.log(err)
    }
  }, [markaazType])

  const handleClose = () => {
    onClose()
    setCancelSubscription(false)
  }

  useEffect(() => {
    fetchSubscriptions()
  }, [fetchSubscriptions])

  useEffect(() => {
    if (isOpen === true) {
      document.documentElement.style.overflow = 'hidden'
    }
    return () => {
      document.documentElement.style.overflow = null
    }
  }, [isOpen])

  if (dashboardInfo.user_info.badged === true) {
    return (
      <Box paddingBottom="3" sx={sx.root}>
        <Box style={{ display: 'flex' }} sx={sx.bold} marginTop={3}>
          Your monthly subscription is being sponsored by&nbsp;
          <span>
            {dashboardInfo.user_info.epName === 'EPSG' && <p sx={{ fontSize: '14px', fontWeight: 'bold' }}>EPSG</p>}
          </span>
          <span>
            {dashboardInfo.user_info.epName === 'MLS' && (
              <p sx={{ fontSize: '14px', fontWeight: 'bold' }}>Merchant Lynx Services</p>
            )}
          </span>
          <span style={{ marginLeft: '30px' }}>
            Activated on {moment(dashboardInfo.user_info.epSubActivationDate).format('MM/DD/YYYY')}
          </span>
        </Box>
      </Box>
    )
  }

  if (!markaazType) {
    return (
      <Box paddingBottom="3" sx={sx.root}>
        <Box sx={sx.bold} marginTop={3}>
          Your subscription is activated from {subscriptionType}
        </Box>
      </Box>
    )
  }

  return (
    <Box paddingBottom="3" sx={sx.root}>
      {accountType === 'free' ? (
        <Box sx={sx.root}>
          <Box sx={sx.bold} marginBottom="3%">
            Select a plan to manage your subscription
          </Box>
          <Button onClick={onClickStartNow} style={{ marginBottom: '300px' }}>
            Start Now
          </Button>
          <Box color="#212529">
            <ZendeskLink />
          </Box>
        </Box>
      ) : (
        <>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
              <ThreeDotsLoadingSpinner width="60px" />
            </Box>
          ) : (
            <>
              <Box minHeight="370px">
                {Object.keys(activeSubscription)?.length > 0 && (
                  <Box sx={sx.yourPlanContainer}>
                    <Box sx={sx.label}>Your Plan</Box>
                    <Grid container alignItems="center" marginLeft={2} marginTop={4}>
                      <>
                        <Grid item xs={1.5}>
                          <Box sx={sx.bold} fontSize="18px" fontStyle="italic">
                            Premium
                          </Box>
                        </Grid>
                        <Grid item xs={1.7}>
                          <Box sx={sx.bold} fontStyle="italic">
                            {activeSubscription.price}
                          </Box>
                        </Grid>
                        <Grid item xs={3}>
                          <Box sx={sx.text}>Next payment due on {activeSubscription.nextPaymentDue}</Box>
                        </Grid>
                        <Grid item xs={3} className="d-flex align-items-center">
                          <Button type={SECONDARY_BUTTON} negative onClick={onClickCancel}>
                            Cancel
                          </Button>
                        </Grid>
                      </>
                    </Grid>
                  </Box>
                )}
                <Box marginTop={7}>
                  <Box sx={sx.label}>Billing History</Box>
                  {billingHistory.map(transaction => (
                    <Grid container alignItems="center" marginLeft={2} marginTop={4}>
                      <Grid item xs={1.5} fontStyle="italic">
                        <Box sx={sx.text}>
                          {transaction.title.includes('Promo') ? transaction.title : 'Premium'}
                        </Box>
                      </Grid>
                      <Grid item xs={1.7}>
                        <Box sx={sx.text}>{transaction.price}</Box>
                      </Grid>
                      <Grid item xs={4}>
                        <Box sx={sx.text}>
                          {transaction.title.includes('Promo') ? 'Activated on ' : 'Paid on '}
                          {transaction.paidOn}
                        </Box>
                      </Grid>
                    </Grid>
                  ))}
                </Box>
              </Box>
              <Box color="#212529" marginTop="25px">
                <ZendeskLink />
              </Box>
            </>
          )}
        </>
      )}
      <Dialog fullScreen open={isOpen} onClose={handleClose} sx={sx.root}>
        <Box>
          <Grid container justifyContent="space-between">
            <Grid item margin={2}>
              <img
                className="m-2"
                src="https://dashboard.app.markaaz.com/static/media/markaaz_logo_blue.043fce4d.png"
                height={25}
                alt="logo"
                onClick={() => navigate('/')}
                style={{ cursor: 'pointer' }}
              />
            </Grid>
            <Grid item margin={2}>
              <Grid container sx={sx.close} flexDirection="row" onClick={handleClose}>
                <Button onClick={() => (onClose ? onClose() : navigate(-1))} style={{ marginRight: '10px' }}>
                  Exit
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Box>
        <Box marginTop={7}>
          <Grid container justifyContent="space-between">
            <Grid
              item
              margin={2}
              xs={12}
              md={6}
              sx={{
                padding: '0px 10px',
              }}
              className="mx-auto"
            >
              {!cancelSubscription ? (
                <UnCancelSubscriptionContent onClose={onClose} onCanceled={() => handleCancel()} />
              ) : (
                <CancelSubscriptionSuccessContent onClose={handleClose} />
              )}
            </Grid>
          </Grid>
        </Box>
      </Dialog>
    </Box>
  )
}

export default Subscription
